import { Box, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import classNames from 'classnames';
import type React from 'react';
import { useEffect, useState } from 'react';
import ExpertoBayerLogo from '/@/components/new-login/assets/expertobayer-logo.svg?url';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import colors from '../theme-provider/colors';
import styles from './styles.module.css';

type Country = {
    code: string;
    name: string;
    id: number;
    redirectToExpertoBayer?: boolean;
    [key: string]: unknown;
};

type SelectedCountry = {
    code: string;
    name: string;
    [key: string]: unknown;
};

type CountryMenuProps = {
    countries: Country[];
    selectedCountry: SelectedCountry;
    onChange: (country: Country) => void;
    openUp?: boolean;
};

const CountryMenu: React.FC<CountryMenuProps> = ({ countries, selectedCountry, onChange, openUp = false }) => {
    const [expanded, setExpanded] = useState(false);
    const [popupIsOpen, setPopupIsOpen] = useState(false);

    useEffect(() => {
        return () => {
            handleClose();
        };
    }, []);

    const handleOpen = () => {
        if (countries.length > 1) {
            setExpanded(true);
        }
    };

    const handleClose = () => {
        setExpanded(false);
    };

    const handleClick = (country: Country) => () => {
        if (country.redirectToExpertoBayer) {
            setPopupIsOpen(true);
        } else {
            localStorage.setItem('country-setted', country.id.toString());
            onChange(country);
        }
        handleClose();
    };

    const renderFlag = (code: string) => {
        return <div className={classNames(styles.flag, styles[code])} />;
    };

    const fakeCountries: Country[] = [
        { name: 'Argentina', code: 'AR', id: 1, redirectToExpertoBayer: true },
        { name: 'Uruguay', code: 'UY', id: 9663, redirectToExpertoBayer: true },
    ];

    let allCountries = countries;
    if (settings.app.theme === 'cultivio') {
        allCountries = allCountries
            .concat(fakeCountries)
            .filter((country, index, self) => index === self.findIndex((c) => c.id === country.id)); // Guarda para que no se dupliquen si pasa a Prod antes de la migracion de AppInstances
    }

    return (
        <>
            <div
                className={classNames(styles.root, countries.length > 1 && styles.clickable)}
                tabIndex={0}
                onBlur={handleClose}
            >
                <div className={styles.selected} onClick={handleOpen}>
                    {renderFlag(selectedCountry.code)}
                </div>
                {expanded && (
                    <div className={classNames(styles.options, { [styles.openUp]: openUp })}>
                        {allCountries.map((country) => (
                            <div key={country.code} className={styles.option} onClick={handleClick(country)}>
                                {renderFlag(country.code)}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <Dialog
                open={popupIsOpen}
                onClose={() => setPopupIsOpen(false)}
                aria-labelledby='modal-modal-title'
                aria-describedby='modal-modal-description'
            >
                <DialogContent>
                    <Stack
                        direction={'column'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        spacing={2}
                        sx={{ textAlign: 'center' }}
                    >
                        <Typography
                            sx={{
                                color: colors.themes.expertobayer.primary.default,
                            }}
                            fontWeight={'bold'}
                        >
                            {_('Ahora en Argentina y Uruguay somos')}
                        </Typography>

                        <Box width={{ xs: '100%', sm: '60%' }}>
                            <img alt={'Experto Bayer'} src={ExpertoBayerLogo} />
                        </Box>
                        <Typography
                            sx={{
                                color: colors.themes.expertobayer.primary.default,
                            }}
                        >
                            {_(
                                'Serás redirigido automáticamente a expertobayer.com y podrás ingresar con tus mismas credenciales para que puedas seguir disfrutando de nuestros servicios y recomendaciones.'
                            )}
                        </Typography>
                        <Typography
                            sx={{
                                color: colors.themes.expertobayer.primary.default,
                            }}
                            fontSize={'small'}
                        >
                            {_('Si deseas ingresar desde otros países, podés continuar utilizando Cultivio.')}
                        </Typography>
                        <Button
                            variant='contained'
                            sx={{
                                backgroundColor: colors.themes.expertobayer.primary.default,
                                '&:hover': { backgroundColor: colors.themes.expertobayer.primary.default },
                            }}
                            onClick={() => {
                                window.location.replace('https://expertobayer.com');
                            }}
                        >
                            {_('Aceptar')}
                        </Button>
                        <Button
                            sx={{ color: colors.themes.expertobayer.primary.default }}
                            variant='text'
                            onClick={() => setPopupIsOpen(false)}
                        >
                            {_('Seguir en Cultivio')}
                        </Button>
                    </Stack>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default CountryMenu;
